<template>
  <slot
    v-if="route.meta.subpage === 'ProductCatalogueForm'"
    :id="id"
    :loading="loading"
    :is-update-form="isUpdateForm"
  />
  <section v-else class="d-flex flex-column h-100">
    <DataTable
      class="shadow-sm"
      :loading="loading"
      :rows="state?.items"
      :cols-map="colsMap"
      sortable
      selectable
      searchable
      createable
      :updatable-inline="loading ? false : true"
      deletable
      :deletable-inline="loading ? false : true"
      paginated
      :count="count"
      :page="currentPage"
      :total="state?.total"
      :limit="state?.limit"
      :error-message="errorMessage"
      @create="router.push({path: 'create'})"
      @delete="(ids: string[]) => deleteDialogOpen(ids)"
      @delete-inline="(row) => deleteDialogOpen([String(row.id)])"
      @update-inline="(row) => router.push({path: `update/${row.id}`})"
      @update:page="(offset: number) => updateOffset(offset)"
      @update:page-limit="updatePageLimit"
      @search="
        (value: string) => {
          search = value;
          fetchData(0);
        }
      "
    />
    <DeleteDialog
      :open="deleteDialogIsOpen"
      :loading="loading"
      @delete="deleteItem"
      @close="deleteDialogIsOpen = false"
    />
  </section>
</template>
<script lang="ts" setup generic="T extends {id: number; name: string}">
import {GenericPagination} from '@/stores/userManagement/userManagement';
import {scrollTop} from '@/sphere/scroll';

interface Props {
  title: string;
  localStorageKey: string;
  state: GenericPagination<T[]> | null;
  set: (value: any) => void;
  read: ({offset, limit, search}: {offset: number; limit: number; search?: string}) => unknown;
  delete: (id: number) => unknown;
  colsMap: {[key: string]: string};
  cached: boolean;
}

const props = withDefaults(defineProps<Props>(), {});

const route = useRoute();
const router = useRouter();

const isUpdateForm = computed(() => route.path.includes('/update'));
const id = computed(() => Number(route.params.id)) as ComputedRef<number>;

const emits = defineEmits<{cached: []}>();

const search = ref('');
const loading = ref(true);
const errorMessage = ref('');
const deleteDialogIsOpen = ref(false);
const idsToDelete = ref<string[]>([]);

const pageLimit = ref(25);
const currentOffset = ref(0);

const calculatedPage = computed(() =>
  props.state !== null ? Math.ceil((props.state.offset - 1) / props.state.limit) + 1 : 1,
);
const currentPage = ref(calculatedPage.value);

const count = computed(() =>
  props.state !== null ? Math.ceil(props.state.total / props.state.limit) : 1,
);

const deleteDialogOpen = (ids: string[]) => {
  deleteDialogIsOpen.value = true;
  idsToDelete.value = ids;
};

/**
 * Deletes a product with deleteProduct in products store
 */
const deleteItem = async () => {
  const promises = idsToDelete.value.map((id) => props.delete(Number(id)));
  try {
    loading.value = true;
    await Promise.all(promises);
  } catch (error: any) {
    errorMessage.value = error;
  } finally {
    deleteDialogIsOpen.value = false;
    loading.value = false;
  }
};

/**
 * Makes requests for generic data
 */
const fetchData = async (offset: number) => {
  try {
    loading.value = true;
    currentOffset.value = offset;
    await props.read({
      offset: currentOffset.value,
      limit: pageLimit.value,
      search: search.value,
    });
  } catch (error: any) {
    errorMessage.value = error;
  } finally {
    loading.value = false;
  }
};

const updatePageLimit = (size: number) => {
  pageLimit.value = size;
  fetchData(0);
};

const updateOffset = async (offset: number) => {
  try {
    await fetchData(offset);
  } catch (error: any) {
    errorMessage.value = error;
  } finally {
    scrollTop();
  }
};

/**
 * Fetches tokens & data; called before the component is inserted into the DOM
 */
const startGenericTableView = async () => {
  if (props.cached === false) await fetchData(0);
  loading.value = false;
  emits('cached');
};

onMounted(() => startGenericTableView());
</script>
